import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
} from "react";
import {
  LevelTestQuestion,
  LevelTestAnswer,
  LevelTestResult,
} from "@/services/student/level-test.interfaces";

// Estado do contexto
interface LevelTestState {
  // Sessão atual
  sessionId?: string;
  currentQuestionIndex: number;
  
  // Histórico de questões respondidas (readonly)
  questionsHistory: LevelTestQuestion[];
  
  // Questão atual (editável)
  currentQuestion?: LevelTestQuestion;
  
  answers: LevelTestAnswer[];

  // Status
  isTestCompleted: boolean;

  // Resultado
  result?: LevelTestResult;

  // Estado local
  lastSavedAt?: string;
}

// Ações do reducer
type LevelTestAction =
  | { type: "SET_SESSION_ID"; payload: { sessionId: string } }
  | { type: "ADD_TO_HISTORY"; payload: { question: LevelTestQuestion } }
  | { type: "SET_CURRENT_QUESTION"; payload: { question: LevelTestQuestion } }
  | { type: "SUBMIT_ANSWER"; payload: { answer: LevelTestAnswer } }
  | { type: "COMPLETE_TEST"; payload: { result: LevelTestResult } }
  | { type: "RESET_TEST" };

// Contexto
interface LevelTestContextType {
  state: LevelTestState;
  actions: {
    setSessionId: (sessionId: string) => void;
    addToHistory: (question: LevelTestQuestion) => void;
    setCurrentQuestion: (question: LevelTestQuestion) => void;
    submitAnswer: (answer: LevelTestAnswer) => void;
    completeTest: (result: LevelTestResult) => void;
    resetTest: () => void;
  };
}

const LevelTestContext = createContext<LevelTestContextType | null>(null);

// Estado inicial
const initialState: LevelTestState = {
  currentQuestionIndex: 0,
  questionsHistory: [],
  currentQuestion: undefined,
  answers: [],
  isTestCompleted: false,
};

// Reducer
function levelTestReducer(
  state: LevelTestState,
  action: LevelTestAction
): LevelTestState {
  switch (action.type) {
    case "SET_SESSION_ID":
      return {
        ...state,
        sessionId: action.payload.sessionId,
        lastSavedAt: new Date().toISOString(),
      };

    case "ADD_TO_HISTORY":
      return {
        ...state,
        questionsHistory: [...state.questionsHistory, action.payload.question],
        lastSavedAt: new Date().toISOString(),
      };

    case "SET_CURRENT_QUESTION":
      return {
        ...state,
        currentQuestion: action.payload.question,
        lastSavedAt: new Date().toISOString(),
      };

    case "SUBMIT_ANSWER": {
      const submitState = {
        ...state,
        answers: [...state.answers, action.payload.answer],
        currentQuestionIndex: state.currentQuestionIndex + 1,
        lastSavedAt: new Date().toISOString(),
      };

      return submitState;
    }

    case "COMPLETE_TEST": {
      const completeState = {
        ...state,
        isTestCompleted: true,
        result: action.payload.result,
        lastSavedAt: new Date().toISOString(),
      };

      return completeState;
    }

    case "RESET_TEST":
      return {
        ...initialState,
        lastSavedAt: new Date().toISOString(),
      };

    default:
      return state;
  }
}

// Provider
export const LevelTestProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(levelTestReducer, initialState);

  const setSessionId = useCallback((sessionId: string) => {
    dispatch({
      type: "SET_SESSION_ID",
      payload: { sessionId },
    });
  }, []);

  const addToHistory = useCallback((question: LevelTestQuestion) => {
    console.log("🔍 [CONTEXT] addToHistory - Adding question to history:", {
      questionId: question.id,
      questionText: question.questionText,
      optionsCount: question.options?.length || 0,
      currentHistoryCount: state.questionsHistory.length
    });
    
    dispatch({
      type: "ADD_TO_HISTORY",
      payload: { question },
    });
  }, [state.questionsHistory.length]);

  const setCurrentQuestion = useCallback((question: LevelTestQuestion) => {
    console.log("🔍 [CONTEXT] setCurrentQuestion - Setting current question:", {
      questionId: question.id,
      questionText: question.questionText,
      questionType: question.questionType,
      difficultyLevel: question.difficultyLevel,
      skillArea: question.skillArea,
      optionsCount: question.options?.length || 0,
      options: question.options?.map(opt => ({
        id: opt.id,
        text: opt.text,
        order: opt.order
      })) || []
    });
    
    dispatch({
      type: "SET_CURRENT_QUESTION",
      payload: { question },
    });
  }, []);

  const submitAnswer = useCallback((answer: LevelTestAnswer) => {
    dispatch({
      type: "SUBMIT_ANSWER",
      payload: { answer },
    });
  }, []);

  const completeTest = useCallback((result: LevelTestResult) => {
    dispatch({
      type: "COMPLETE_TEST",
      payload: { result },
    });
  }, []);

  const resetTest = useCallback(() => {
    dispatch({ type: "RESET_TEST" });
  }, []);


  const contextValue: LevelTestContextType = {
    state,
    actions: {
      setSessionId,
      addToHistory,
      setCurrentQuestion,
      submitAnswer,
      completeTest,
      resetTest,
    },
  };

  return (
    <LevelTestContext.Provider value={contextValue}>
      {children}
    </LevelTestContext.Provider>
  );
};

// Hook para usar o contexto
export const useLevelTestContext = () => {
  const context = useContext(LevelTestContext);
  if (!context) {
    throw new Error(
      "useLevelTestContext must be used within a LevelTestProvider"
    );
  }
  return context;
};

// Hook de conveniência para acessar apenas o estado
export const useLevelTestState = () => {
  const { state } = useLevelTestContext();
  return state;
};

// Hook de conveniência para acessar apenas as ações
export const useLevelTestActions = () => {
  const { actions } = useLevelTestContext();
  return actions;
};
